import { fromJS, List } from "immutable"
import moment from "moment"

// helpers
import { getNestedObjectKey } from "shared/imports/sharedHelperFunctions"

// action types
import actionTypes, {
    loadDashboardActionDict,
    loadDashboardTemplateListActionDict,
    loadWidgetTemplateListActionDict,
} from "app/static/frontend/dashboards/action-types"

export const dashboardsDefault = ({
    cached_widget_data,
    creator,
    external_password,
    external_regions,
    external_slug,
    externalDownloadsDisabled,
    globalDateCustomFrom,
    globalDateCustomTo,
    is_external,
    is_limited_to_creator,
    layout,
    limitedToTeams,
    name,
    widgets,
}) =>
    fromJS({
        cached_widget_data,
        compactType: DjangIO.app.widgets.types.ReactGridLayoutTypes.vertical.value,
        creator,
        deletedWidgets: [],
        editing: false,
        editingTeamPermissions: false,
        external_password,
        external_regions,
        external_slug,
        externalDownloadsDisabled,
        globalDateCustomFrom: globalDateCustomFrom || moment(),
        globalDateCustomTo,
        hasLoaded: true,
        is_external,
        is_limited_to_creator,
        layout: layout || [],
        limited_to_teams: limitedToTeams,
        name,
        widgets,
    })

const initialDashboardFormState = fromJS({
    page: 1,
    selectedTemplate: null,
})

export function getInitialState(kwargs = {}) {
    return fromJS({
        createDashboardModalOpen: false,
        createDashboardForm: initialDashboardFormState,
    }).merge(kwargs)
}

const initialState = getInitialState()

const getInitialGlobalDateCustomToFrom = (action) => {
    const widgetValues = action.widgets && Object.values(action.widgets)

    for (const widgetValue of widgetValues) {
        if (widgetValue.data_sources) {
            for (const dataSource of widgetValue.data_sources) {
                if (dataSource.safed_search) {
                    const dataType = dataSource.safed_search.data_type
                    const additionalFilters = dataSource.additional_filters

                    if (additionalFilters && Object.keys(additionalFilters).length) {
                        const customDateField =
                            DjangIO.app.search.types.QuorumDataTypeOrdering.by_data_type_default_date[dataType]

                        const globalDateCustomFrom = additionalFilters[`${customDateField}__gt`]
                        const globalDateCustomTo = additionalFilters[`${customDateField}__lt`]

                        return globalDateCustomFrom && globalDateCustomTo
                            ? {
                                  globalDateCustomFrom: moment(globalDateCustomFrom),
                                  globalDateCustomTo: moment(globalDateCustomTo),
                              }
                            : {}
                    }
                }
            }
        }
    }

    return {}
}

export const dashboardsReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.DESTROY_DASHBOARD_STORE:
            return state.deleteIn([action.id])

        case actionTypes.INITIALIZE_DASHBOARD:
            // if we have an existing layout
            // (we have already fired the async request to populate this prop)
            // and we are not deliberately resetting the dashboards store
            // (segue-ing between the dashboard and other parts of the site)
            // then just return the extant slice of the dashboards store
            if (state.getIn([action.id, "layout"]) && !action.reset) {
                return state
            }

            return state.setIn(
                [action.id],
                dashboardsDefault({
                    cached_widget_data: action.cached_widget_data,
                    creator: action.creator,
                    externalDownloadsDisabled: action.externalDownloadsDisabled,
                    external_slug: action.external_slug,
                    is_external: action.is_external,
                    external_password: action.external_password,
                    external_regions: action.external_regions,
                    is_limited_to_creator: action.is_limited_to_creator,
                    limitedToTeams: action.limitedToTeams,
                    name: action.name,
                    widgets: action.widgets,
                    // if we pass an initial layout (from the GET request), set that
                    ...(action.layout ? { layout: action.layout } : {}),
                    ...getInitialGlobalDateCustomToFrom(action),
                }),
            )

        // will merge and update the layout while removing the given key (index) from it
        case actionTypes.REMOVE_FROM_DASHBOARD_LAYOUT:
            return state.mergeIn(
                [action.id],
                fromJS({
                    deletedWidgets: state.getIn([action.id, "deletedWidgets"]).push(action.index),
                    // special case:
                    // props.removeFromDashboardLayout() from [...] to [],
                    // which should unmount the ReactGridLayout and instead mount the EmptyState

                    // this means that the onLayoutChange does not fire, so the newLayout
                    // will be out of date with the layout
                    ...(state.getIn([action.id, "newLayout"]) &&
                    state
                        .getIn([action.id, "newLayout"])
                        .filter((item) => item.get("i") !== action.index)
                        .equals(List())
                        ? {
                              layout: state
                                  .getIn([action.id, "newLayout"])
                                  .filter((item) => item.get("i") !== action.index),
                          }
                        : {}),
                    // either we have already dragged or resized
                    newLayout: (
                        state.getIn([action.id, "newLayout"]) ||
                        // or we are removing without having dragged or resized
                        state.getIn([action.id, "layout"])
                    ).filter((item) => item.get("i") !== action.index),
                    widgets: state.getIn([action.id, "widgets"]).remove(action.index),
                }),
            )

        case actionTypes.RECONCILE_DASHBOARD_LAYOUT:
            if (state.getIn([action.id, "newLayout"])) {
                return state.mergeIn(
                    [action.id],
                    fromJS({
                        layout: state.getIn([action.id, "newLayout"]),
                    }),
                )
            }

            return state

        // will merge and update the given action values in the dashboard store
        case actionTypes.SET_DASHBOARD_VALUES:
            return state.mergeIn([action.id], fromJS(action.values))

        // will set the isLoading state when the createOrUpdateDashboard action creator fires
        case loadDashboardActionDict.start:
            return state.mergeIn(
                [action.id],
                fromJS({
                    isLoading: true,
                }),
            )

        // will update the isLoading state when the createOrUpdateDashboard action creator successfully completes
        case loadDashboardActionDict.success:
            return state.mergeIn(
                [action.id],
                fromJS({
                    isLoading: false,
                }),
            )

        case actionTypes.TOGGLE_CREATE_DASHBOARD_MODAL:
            if (action.page) {
                return state.set("createDashboardModalOpen", true).setIn(["createDashboardForm", "page"], action.page)
            }

            return state.set("createDashboardModalOpen", false).setIn("createDashboardForm", initialDashboardFormState)

        case actionTypes.SELECT_DASHBOARD_FORM_TEMPLATE:
            return state.setIn(["createDashboardForm", "selectedTemplate"], action.template)

        case loadWidgetTemplateListActionDict.start:
            return state.mergeIn(
                ["widgetTemplates"],
                fromJS({
                    isLoading: true,
                }),
            )

        case loadDashboardTemplateListActionDict.start:
            return state.mergeIn(
                ["dashboardTemplates"],
                fromJS({
                    isLoading: true,
                }),
            )

        case loadDashboardTemplateListActionDict.success:
        case loadWidgetTemplateListActionDict.success:
            const key =
                action.type === loadWidgetTemplateListActionDict.success ? "widgetTemplates" : "dashboardTemplates"
            const existingTemplates = state.getIn([key, "templates"], fromJS([])).toJS()
            const responseTemplates = getNestedObjectKey(action, ["response", "objects"])

            // if the server returns fewer objects than we asked for,
            // we must have gotten all the templates already
            const endOfList = responseTemplates.length < action.limit

            // TODO store results in store keyed by quick_search filter
            // (or at least store the default filter) to avoid period
            // of empty store when clearing search bar / stored template list

            // only add widget templates we didn't already have to the store
            const newTemplates = responseTemplates.filter(
                (newTemplate) =>
                    !Boolean(
                        // look for the new widget in the list of widgets we already have
                        existingTemplates.find((oldTemplate) => oldTemplate.id === newTemplate.id),
                    ),
            )

            const newTemplateList = fromJS(existingTemplates.concat(newTemplates))

            return state.setIn(
                [key],
                fromJS({
                    isLoading: false,
                    templates: newTemplateList,
                    hasMore: !endOfList,
                }),
            )

        case actionTypes.WIDGET_CREATE_FORM_CLEAR_TEMPLATE_LIST:
            return state.setIn(["widgetTemplates", "templates"], fromJS([]))

        case actionTypes.DASHBOARD_CLEAR_TEMPLATE_LIST:
            return state.setIn(["dashboardTemplates", "templates"], fromJS([]))

        default:
            return state
    }
}

export default dashboardsReducer
